import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import { useDemoData } from "@/hooks/useDemoData";

interface EditDriverModalProps {
  driver: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (driverId: string, data: any) => void;
}

export default function EditDriverModal({
  driver,
  isOpen,
  onClose,
  onSave,
}: EditDriverModalProps) {
  const { trucks } = useDemoData();
  const { register, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      name: "",
      currentTruck: "",
      contact: 0,
    },
  });

  // Update form values when driver prop changes
  useEffect(() => {
    if (driver && isOpen) {
      setValue("name", driver.name || "");
      setValue("currentTruck", driver.currentTruck || "");
      setValue("contact", driver.contact || 0);
    }
  }, [driver, isOpen, setValue]);

  const onSubmit = (data: any) => {
    onSave(driver.id, data);
    onClose();
    reset();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Edit Driver</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Driver Name</label>
            <Input
              type="text"
              {...register("name", { required: true })}
              placeholder="Driver name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Current Truck</label>
            <select
              {...register("currentTruck", { required: true })}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">Select a truck</option>
              {trucks.map((truck) => (
                <option key={truck.id} value={truck.id}>
                  {truck.id} - {truck.model}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Contact Number</label>
            <Input
              type="number"
              {...register("contact", { required: true })}
              placeholder="Contact number"
            />
          </div>

          <div className="flex space-x-2 pt-4">
            <Button type="submit" className="flex-1">
              Save Changes
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}